import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from 'hooks/useRedux';
import { authActions, authSelectors } from 'state/auth';
import { FIELD_MISSING, VALUE_TOO_LONG } from 'constants/validationMessages';
import ErrorBadge from 'components/ErrorBadge/ErrorBadge';
import Alert from 'react-bootstrap/Alert';
import store, { useAppDispatch } from 'state/store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Form } from 'react-bootstrap';
import NameHeader from 'components/nameHeader/NameHeader';
import InfoBar from 'components/InfoBar/InfoBar';
import { ConfigurableHyperLinkSelector } from 'state/hyperlink/hyperlink.selectors';
import ColoredLine from 'components/ColoredLine/ColoredLine';
import WorkflowNavigationButton from 'components/Buttons/WorkflowNavigationButton/WorkflowNavigationButton';
import './ResetPassword.scss';
import { CheckSquareIcon } from 'assets/icons/CheckSquareIcon';
import { ExclamationSquareIcon } from 'assets/icons/ExclamationSquareIcon';

interface DefaultValues {
  password: string;
  confirmPassword: string;
}
function useQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

const ResetPassword = () => {
  const hyperlinks = useAppSelector(ConfigurableHyperLinkSelector);

  const navigate = useNavigate();
  const error = useAppSelector(authSelectors.resetPasswordErrorSelector);
  const query = useQuery();
  const [success, setSuccess] = useState<boolean>(false);
  const [isLoader, setIsLoader] = useState(false);

  const defaultValues = { password: '', confirmPassword: '' };

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({ defaultValues });

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(authActions.setResetPasswordError(''));
  }, []);

  const setInputStyles = (key: 'password' | 'confirmPassword') =>
    errors[key] ? 'form-control border border-danger border-opacity-25 border-2' : 'form-control';

  const onSubmit = async (data: DefaultValues) => {
    setIsLoader(true);
    if (password !== confirmPassword) {
      dispatch(authActions.setResetPasswordError('Passwords do not match'));
      setIsLoader(false);
      return;
    }
    await dispatch(
      authActions.resetPassword({ token: query.get('token') || '', password: data.password }),
    );
    if (!store.getState().auth.resetPasswordError) {
      setSuccess(true);
      reset();
    }
    setIsLoader(false);
  };

  return (
    <div className="w-100">
      <NameHeader
        serviceType={false}
        disableLogoOnClick={true}
      />
      {!error && success && (
        <Alert
          variant={'success'}
          className="text-center alert-success small mb-0"
        >
          <div className="d-inline-flex align-items-center gap-3">
            <div className="d-flex align-items-center">
              <CheckSquareIcon />
            </div>{' '}
            Password changed
          </div>
        </Alert>
      )}

      {error && (
        <Alert
          variant={'danger'}
          className="text-center small mb-0 d-flex justify-content-center align-items-center"
        >
          <div className="d-flex flex-col align-items-center gap-3">
            <div className="d-flex align-items-center">
              <ExclamationSquareIcon />
            </div>
            <div className="d-flex flex-column align-items-start">
              {Array.isArray(error)
                ? error.map((errorMessage, key) => {
                    return <div key={key}>{errorMessage}</div>;
                  })
                : error}
            </div>
          </div>
        </Alert>
      )}

      <div className="w-100 reset-password-wrapper">
        <div className="bg-light-purple">
          <ColoredLine />
          <div className="p-4 border-bottom border-4 border-white">
            <h2 style={{ fontWeight: 700 }}>
              {success ? 'Password changed successfully' : ' Reset Password'}
            </h2>
            <div style={{ display: `${success ? 'none' : 'block'}` }}>
              Set new password value for {query.get('email')}
            </div>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <div
                className="mt-2"
                style={{ display: `${success ? 'none' : 'block'}` }}
              >
                <Form.Group className="mb-3">
                  <Form.Label className="text-black">
                    New Password<span className="text-danger"> *</span>
                  </Form.Label>
                  <Form.Control
                    id="reset-password-input"
                    disabled={success}
                    type="password"
                    className={`${setInputStyles('password')} form-control rounded-pill`}
                    {...register('password', {
                      required: {
                        value: true,
                        message: FIELD_MISSING,
                      },
                      maxLength: {
                        value: 35,
                        message: VALUE_TOO_LONG,
                      },
                      onChange: () => {
                        if (error) {
                          dispatch(authActions.setResetPasswordError(''));
                        }
                      },
                    })}
                  />
                  <ErrorBadge
                    errors={errors}
                    formKey="password"
                  />
                </Form.Group>
              </div>
              <div
                className="mt-2"
                style={{ display: `${success ? 'none' : 'block'}` }}
              >
                <Form.Group className="mb-3">
                  <Form.Label className="text-black">
                    Confirm New Password<span className="text-danger"> *</span>
                  </Form.Label>
                  <Form.Control
                    id="confirm-password-reset-input"
                    disabled={success}
                    type="password"
                    className={`${setInputStyles('confirmPassword')} form-control rounded-pill`}
                    {...register('confirmPassword', {
                      required: {
                        value: true,
                        message: FIELD_MISSING,
                      },
                      maxLength: {
                        value: 35,
                        message: VALUE_TOO_LONG,
                      },
                      onChange: () => {
                        if (error) {
                          dispatch(authActions.setResetPasswordError(''));
                        }
                      },
                    })}
                  />
                  <ErrorBadge
                    errors={errors}
                    formKey="confirmPassword"
                  />
                </Form.Group>
              </div>
              <WorkflowNavigationButton
                id="reset-password-button"
                text={success ? 'Go to Login page' : 'Reset Password'}
                variant="danger"
                className={`${success ? 'mt-2' : 'mt-1'}`}
                onClick={() => {
                  if (success) {
                    navigate('/');
                  }
                }}
              >
                {isLoader && (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    color="white"
                    className="mx-1"
                    spinPulse={true}
                  />
                )}
              </WorkflowNavigationButton>
            </Form>
          </div>

          {!success && (
            <InfoBar center={true}>
              Having trouble resetting your password?{' '}
              <a
                id="chat-with-us-anchor-login"
                className="text-purple text-decoration-none"
                href={hyperlinks['support_chat_url']}
                target="_blank"
              >
                Chat with us
              </a>{' '}
              or contact support at (855) 863-1355.
            </InfoBar>
          )}
        </div>
      </div>
    </div>
  );
};
export default ResetPassword;
